import { supabase } from "../lib/supabase";
import { extractFunctionErrorMessage } from "./edgeFunctionError";

/**
 * All data-export actions are routed through the shared portal-api Edge
 * Function's "data_export" resource, which proxies to portal-api's existing
 * /integrations endpoints. The caller must supply the advertiser_id and an
 * auth token from the session.
 */
async function invoke(action, advertiserId, authToken, extra = {}) {
  if (!supabase) {
    throw new Error("Supabase client is not initialized");
  }
  const { data, error } = await supabase.functions.invoke("portal-api", {
    body: { resource: "data_export", action, advertiser_id: advertiserId, ...extra },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (error) {
    throw new Error(await extractFunctionErrorMessage(error, "portal-api function error"));
  }
  return data;
}

/**
 * Returns the current "export" integration row for the advertiser, or null
 * if it has never been set up. Uses portal-api's list endpoint, which does
 * not decode credentials — use getIntegration to prefill an edit form.
 * Shape: { integration: { uuid, type, location, status } | null }
 */
export async function getStatus(advertiserId, authToken) {
  const data = await invoke("status", advertiserId, authToken);
  return data.integration;
}

/**
 * Creates (no integrationUuid) or updates (with integrationUuid) the
 * advertiser's S3/GCS data export destination. portal-api performs a
 * synchronous live connection test before persisting — expect this to take
 * a few seconds and to fail with a descriptive error if credentials/bucket
 * are invalid.
 */
export async function save(advertiserId, authToken, { type, location, credentials, status, integrationUuid }) {
  const data = await invoke("save", advertiserId, authToken, {
    type,
    location,
    credentials,
    status,
    integration_uuid: integrationUuid,
  });
  return data.integration;
}

/**
 * Fetches the full integration row including decoded credentials, to
 * prefill the edit form. Unlike getStatus (which uses portal-api's list
 * endpoint), this hits the single-integration GET, which is the only
 * endpoint that decodes stored credentials.
 */
export async function getIntegration(advertiserId, authToken, { integrationUuid }) {
  const data = await invoke("get", advertiserId, authToken, {
    integration_uuid: integrationUuid,
  });
  return data.integration;
}
